import { SummaryValidation } from '@components/prereg_validation/summary_validation'
import { Alert, AlertTitle, Box, Stack, Typography } from '@mui/material'
import { useSessionContext } from '@providers/session'
import { isUserLoggedIn } from '@providers/session/helpers'
import { MAX_WIDGET_SIZE } from '@shared/constants'
import { i18WithDefault as t } from '@shared/locale'
import { useRoutes } from 'app/use_routes'
import {
  allowNoPayments,
  FlexCardDisplay,
  getOrderPrimaryServiceTypeName,
  getPoliciesData,
  InvoiceCard,
  isVisitOrder,
  PurchaseReceipt,
  Spacer3,
  SurchargePolicy,
} from 'components-ui'
import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Hooks, Types } from 'service-api'
import { PurchaseSummarySkeleton } from './purchase_summary_skeleton'

const bookingCardForST = ['boarding', 'daycare', 'grooming', 'temper-test']

export const PurchaseSummary = () => {
  const sessionCtx = useSessionContext()
  const { navigateRoute } = useRoutes()
  const { accountName = '', bookingId = '' } = useParams()
  const [order, setOrder] = useState<Types.OrderClientDto | undefined>()
  const {
    data: orderResponse,
    isFetching: isOrderLoading,
    isSuccess: isOrderSuccess,
    refetch: refetchOrder,
    error: orderError,
  } = Hooks.useClientOrder(accountName, bookingId)
  const {
    data: receipt,
    isFetching: isReceiptLoading,
    refetch: refetchReceipt,
  } = Hooks.useClientReceipt(accountName, bookingId)

  const isVisit = isVisitOrder(order)
  const bookingConfs = Hooks.useClientBookingConfigs(accountName, order?.primaryServiceType?.serviceType?.name || '', {
    enabled: isVisit,
  })
  const hideRefundPolicy = !isVisit || allowNoPayments(order?.primaryServiceType?.locationBookingConf?.[0])
  const policyCardsData = useMemo(
    () => (receipt ? getPoliciesData(receipt, hideRefundPolicy) : []),
    [receipt, hideRefundPolicy]
  )
  const loggedIn = isUserLoggedIn(sessionCtx.state.user)

  useEffect(() => {
    document.title = t('title.purchaseSummary')
  }, [])

  useEffect(() => {
    if (!isOrderSuccess) {
      return
    }

    if (!loggedIn) {
      setTimeout(
        () =>
          navigateRoute(
            'signIn',
            {},
            {
              state: {
                redirect: {
                  route: 'purchaseSummary',
                  params: { bookingId },
                },
                email: orderResponse?.userData?.[0]?.email,
                userExists: !!(orderResponse.ownerId || orderResponse.owner?.id),
              },
            }
          ),
        0
      )
      return
    }

    setOrder(orderResponse)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderResponse, isOrderSuccess, loggedIn, sessionCtx.state.user?.email])

  useEffect(() => {
    if (orderError) {
      setTimeout(() => navigateRoute('notFound', {}), 0)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderError])

  const showBookingCard = useMemo(() => {
    if (isOrderLoading) {
      return false
    }
    return bookingCardForST.includes(order?.primaryServiceType?.serviceType?.name || '')
  }, [isOrderLoading, order?.primaryServiceType?.serviceType?.name])

  if (isOrderLoading || isReceiptLoading || bookingConfs.isFetching) {
    return <PurchaseSummarySkeleton />
  }

  if (!order?.invoices) {
    return null
  }

  const isGrooming = getOrderPrimaryServiceTypeName(order) === 'grooming'

  return (
    <Stack spacing={3} sx={{ maxWidth: MAX_WIDGET_SIZE, p: 3, pb: '130px', margin: '0 auto' }}>
      <Box>
        <Spacer3 />
        <Typography variant="h4">{t('purchaseSummary.title')}</Typography>
        <SummaryValidation orderId={order.id || ''} />
      </Box>
      <Stack spacing={3}>
        {order.invoices.map((invoice) => (
          <InvoiceCard
            key={invoice.id}
            invoice={invoice}
            order={order}
            onCancelInvoice={() => {
              refetchOrder()
              refetchReceipt()
            }}
          />
        ))}
      </Stack>
      {/* {isIntakeEnabled(bookingConfs.data?.locationBookingConf) && (
        <Intake
          flexBookingConfs={bookingConfs.data?.flexBookingConfs || []}
          order={order || {}}
          onSave={(newOrder) => {
            setOrder(newOrder)
          }}
        />
      )} */}
      <Stack direction="row" flexWrap="wrap" gap={1}>
        <Stack spacing={3} sx={{ flex: '1 0 49%' }}>
          {showBookingCard && (
            <>
              <Box>
                {isGrooming && (
                  <Alert severity="info">
                    <AlertTitle>{t('paymentForm.groomRequestRefund')}</AlertTitle>
                    {t('paymentForm.groomRequestNotice')}
                  </Alert>
                )}
                {policyCardsData.map((group, index) => (
                  <FlexCardDisplay key={index} cardData={group} />
                ))}
              </Box>
              <Box>{bookingConfs.isSuccess && <SurchargePolicy order={order} bookingConfig={bookingConfs.data} />}</Box>
            </>
          )}
        </Stack>
        <Box sx={{ flex: '1 0 50%', height: 'fit-content' }}>
          <PurchaseReceipt receipt={receipt} isVisit={isVisit} order={order} />
        </Box>
      </Stack>
    </Stack>
  )
}
